import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Form,
  Alert,
  Row,
  Col,
  Badge,
  Pagination,
} from "react-bootstrap";
import { productsApi, ingredientsApi } from "../services/services";
import {
  Product,
  Ingredient as ApiIngredient,
  CreateProductDto,
  UpdateProductDto,
} from "../types";
import { SlidePanel, TableControls } from "../components/shared";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import {
  formatDate,
  parseIngredients,
} from "../utils/formatters";
import Flatpickr from "react-flatpickr";
import { useRequiredAuth } from "../components/shared/AuthContext";

interface ProductIngredient {
  id: number;
  name: string;
}

const DEFAULT_INGREDIENT: ProductIngredient = { id: 1, name: "" };
const ProductDetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <p>
    <strong>{label}:</strong> {value}
  </p>
);

const Products: React.FC = () => {
  const { supplierId } = useRequiredAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [availableIngredients, setAvailableIngredients] = useState<
    ApiIngredient[]
  >([]);

  const [currentEditId, setCurrentEditId] = useState<number | null>(null);
  const [productCode, setProductCode] = useState("");
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [ingredients, setIngredients] = useState<ProductIngredient[]>([
    DEFAULT_INGREDIENT,
  ]);
  const [formQuantity, setFormQuantity] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);
  const [position, setPosition] = useState<number | "">("");
  const [useByDate, setUseByDate] = useState<Date | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
    fetchIngredients();

    const onSupplierChange = () => {
      fetchProducts();
      fetchIngredients();
    };
    window.addEventListener("mockSupplierChanged", onSupplierChange);
    return () => window.removeEventListener("mockSupplierChanged", onSupplierChange);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const fetchIngredients = () => {
    ingredientsApi
      .getIngredients()
      .then(setAvailableIngredients)
      .catch(() => { });
  };
  const fetchProducts = () => {
    setLoading(true);
    productsApi
      .getProducts()
      .then((data) => setProducts(data))
      .catch(() => setError("Failed to fetch products"))
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setProductCode("");
    setProductName("");
    setDescription("");
    setIngredients([DEFAULT_INGREDIENT]);
    setFormQuantity(0);
    setPrice(0);
    setWeight(0);
    setPosition("");
    setUseByDate(null);
    setError(null);
    setCurrentEditId(null);
    setIsEditing(false);
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };
  const handleIngredientChange = (id: number, value: string) =>
    setIngredients((prev) =>
      prev.map((ing) => (ing.id === id ? { ...ing, name: value } : ing))
    );
  const handleAddIngredient = () =>
    setIngredients((prev) => [...prev, { id: Date.now(), name: "" }]);
  const handleRemoveIngredient = (id: number) =>
    setIngredients((prev) => prev.filter((ing) => ing.id !== id));

  const handleEdit = (code: string) => {
    const product = products.find((p) => p.productCode === code);
    if (!product) return;
    setCurrentEditId(product.id);
    setProductCode(product.productCode);
    setProductName(product.name);
    setDescription(product.description || "");
    setIngredients(parseIngredients(product.ingredients));
    setFormQuantity(product.quantity);
    setPrice(product.price);
    setWeight(product.weight);
    setPosition(product.position ?? "");
    setUseByDate(product.useByDate ? new Date(product.useByDate) : null);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;
    setLoading(true);
    productsApi
      .deleteProduct(id, "CurrentUser")
      .then(() => {
        showSuccess("Product deleted successfully!");
        setProducts((prev) => prev.filter((p) => p.id !== id));
        fetchProducts();
      })
      .catch(() => setError("Failed to delete product"))
      .finally(() => setLoading(false));
  };

  const handleView = (product: Product) => {
    setSelectedProduct(product);
    setViewModalOpen(true);
  };
  const handleEditQuantity = (product: Product) => {
    setSelectedProduct(product);
    setEditQuantity(product.quantity);
    setQuantityModalOpen(true);
  };
  const handleSaveQuantity = () => {
    if (!selectedProduct) return;
    setLoading(true);
    productsApi
      .updateQuantity(selectedProduct.id, {
        quantity: editQuantity,
        updatedBy: "CurrentUser",
      })
      .then(() => {
        showSuccess("Quantity updated!");
        fetchProducts();
        setQuantityModalOpen(false);
      })
      .catch(() => alert("Failed to update quantity"))
      .finally(() => setLoading(false));
  };

  const handleSave = () => {
    setLoading(true);
    setError(null);
    const ingredientsList = ingredients
      .filter((ing) => ing.name.trim())
      .map((ing) => ing.name);

    const commonData = {
      productCode,
      name: productName,
      description: description || undefined,
      ingredients: JSON.stringify(ingredientsList),
      quantity: formQuantity,
      price,
      weight,
      position: position === "" ? null : Number(position),
      availability: true,
      useByDate: useByDate ? useByDate.toISOString() : new Date().toISOString(),
    };

    let apiCall: Promise<Product | void>;
    if (isEditing && currentEditId) {
      apiCall = productsApi.updateProduct(currentEditId, {
        ...commonData,
        updatedBy: "CurrentUser",
      } as UpdateProductDto);
    } else {
      apiCall = productsApi.createProduct({
        ...commonData,
        supplierId,
        createdBy: "CurrentUser",
      } as CreateProductDto);
    }

    apiCall
      .then(() => {
        showSuccess(
          `Product ${isEditing ? "updated" : "created"} successfully!`
        );
        fetchProducts();
        resetForm();
        setModalOpen(false);
      })
      .catch((err: Error) =>
        setError(
          err?.message || `Failed to ${isEditing ? "update" : "create"} product`
        )
      )
      .finally(() => setLoading(false));
  };

  const handleCancel = () => {
    resetForm();
    setModalOpen(false);
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.productCode.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / entriesPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const displayedProducts = filteredProducts.slice(startIndex, endIndex);
  const startEntry = filteredProducts.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredProducts.length === 0
      ? 0
      : Math.min(startIndex + displayedProducts.length, filteredProducts.length);

  return (
    <PageContainer>
      <div className="d-flex flex-column gap-3">
        <div>
          <PageHeader title="Manage" subTitle="Products" />
        </div>

        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={() => setModalOpen(true)}
            className="px-4 shadow-sm"
          >
            <i className="bi bi-plus-lg me-2"></i>
            New Product
          </Button>
        </div>

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}

      <SlidePanel
        open={modalOpen}
        onClose={handleCancel}
        title={isEditing ? "Edit Product" : "Add New Product"}
        width="500px"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading || !productCode || !productName}
            >
              <i className="bi bi-save me-1"></i> Save
            </Button>
          </>
        }
      >
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        <Form>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Product Code *</Form.Label>
                <Form.Control
                  placeholder="e.g. PRD001"
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Position</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Optional"
                  value={position}
                  onChange={(e) =>
                    setPosition(
                      e.target.value === "" ? "" : parseInt(e.target.value, 10)
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              placeholder="Enter product name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Enter product description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formQuantity}
                  onChange={(e) =>
                    setFormQuantity(parseInt(e.target.value) || 0)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Price (£)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Weight (kg)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.001"
                  value={weight}
                  onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Use By Date</Form.Label>
                <Flatpickr
                  className="form-control"
                  value={useByDate || ""}
                  onChange={([date]) => setUseByDate(date)}
                  options={{ dateFormat: "Y-m-d" }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Ingredients</Form.Label>
            {ingredients.map((ingredient, index) => (
              <div
                key={ingredient.id}
                className="d-flex mb-2 gap-2 align-items-center"
              >
                <Form.Select
                  value={ingredient.name}
                  onChange={(e) =>
                    handleIngredientChange(ingredient.id, e.target.value)
                  }
                  className="flex-grow-1"
                >
                  <option value="">Select Ingredient</option>
                  {availableIngredients.map((ing) => (
                    <option key={ing.id} value={ing.name}>
                      {ing.name}
                    </option>
                  ))}
                </Form.Select>
                {ingredients.length > 1 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveIngredient(ingredient.id)}
                  >
                    <i className="bi bi-dash"></i>
                  </Button>
                )}
                {index === ingredients.length - 1 && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddIngredient}
                  >
                    <i className="bi bi-plus"></i>
                  </Button>
                )}
              </div>
            ))}
          </Form.Group>
        </Form>
      </SlidePanel>

      <SlidePanel
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="Product Details"
        footer={
          <Button variant="secondary" onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        }
      >
        {selectedProduct && (
          <div>
            <ProductDetailRow
              label="Code"
              value={selectedProduct.productCode}
            />
            <ProductDetailRow label="Name" value={selectedProduct.name} />
            <ProductDetailRow
              label="Description"
              value={selectedProduct.description || "N/A"}
            />
            <ProductDetailRow
              label="Status"
              value={
                selectedProduct.availability ? "Available" : "Not Available"
              }
            />
            <ProductDetailRow
              label="Quantity"
              value={selectedProduct.quantity}
            />
            <ProductDetailRow
              label="Price"
              value={`£${selectedProduct.price.toFixed(2)}`}
            />
            <ProductDetailRow
              label="Weight"
              value={`${selectedProduct.weight} kg`}
            />
            <ProductDetailRow
              label="Use By Date"
              value={formatDate(selectedProduct.useByDate)}
            />
          </div>
        )}
      </SlidePanel>

      <SlidePanel
        open={quantityModalOpen}
        onClose={() => setQuantityModalOpen(false)}
        title="Edit Quantity"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setQuantityModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveQuantity}>
              <i className="bi bi-save me-1"></i> Save
            </Button>
          </>
        }
      >
        {selectedProduct && (
          <Form>
            <p>
              <strong>Product:</strong> {selectedProduct.name}
            </p>
            <Form.Group>
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={editQuantity}
                onChange={(e) => setEditQuantity(parseInt(e.target.value) || 0)}
              />
            </Form.Group>
          </Form>
        )}
      </SlidePanel>

        <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-3 p-lg-4">

          <TableControls
            entriesPerPage={entriesPerPage}
            onEntriesChange={setEntriesPerPage}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search products..."
            showSearchIcon
            showEntries={false}
          />

          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead className="small text-uppercase text-secondary">
                <tr>
                  <th className="border-bottom pb-3 pt-2">Code</th>
                  <th className="border-bottom pb-3 pt-2">Name</th>
                  <th className="border-bottom pb-3 pt-2">Status</th>
                  <th className="border-bottom pb-3 pt-2">Quantity</th>
                  <th className="border-bottom pb-3 pt-2">Use By Date</th>
                  <th className="border-bottom pb-3 pt-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      Loading products...
                    </td>
                  </tr>
                ) : displayedProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No products found.
                    </td>
                  </tr>
                ) : (
                  displayedProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="py-3 text-body-secondary">{product.productCode}</td>
                      <td className="py-3 fw-semibold">{product.name}</td>
                      <td>
                        <Badge
                          bg={product.availability ? "info" : "secondary"}
                          pill
                          className="px-3 py-2"
                        >
                          {product.availability ? "Available" : "Not Available"}
                        </Badge>
                      </td>
                      <td>{product.quantity}</td>
                      <td>{formatDate(product.useByDate)}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-secondary"
                            onClick={() => handleView(product)}
                            aria-label="View"
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-secondary"
                            onClick={() => handleEditQuantity(product)}
                            aria-label="Edit quantity"
                          >
                            <i className="bi bi-box"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-secondary"
                            onClick={() => handleEdit(product.productCode)}
                            aria-label="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-danger"
                            onClick={() => handleDelete(product.id)}
                            aria-label="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-top mt-3 pt-3">
            <div className="small text-muted">
              Showing rows {startEntry} to {endEntry} of {filteredProducts.length}
            </div>

            <Pagination size="sm" className="mb-0 ms-md-auto">
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                return (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === safeCurrentPage}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                );
              })}
            </Pagination>
          </div>
        </Card.Body>
      </Card>
      </div>
    </PageContainer>
  );
};

export default Products;
